import { get, patch, postFormData, postJson } from './apiClient';
import { fetchServerPrint } from './serverPrint';
import type { InvoiceCategory } from '../utils/invoiceCategories';
import type { CandidateInvoice } from './reconciliationApi';

export type ClientInvoiceStatus = 'draft' | 'saved' | 'ready' | 'sent' | 'partially_paid' | 'paid' | 'cancelled';

// D56 — one row of the invoice's flattened charges (Transportation,
// Additional charges, each custom expense), src/models/ClientInvoice.js's
// `charges` subdocument shape.
export type ClientInvoiceChargeDto = {
  label: string;
  amount: number;
};

export type ClientInvoiceItemDto = {
  lineNumber?: number | null;
  /** Screen only, never sent: the dispatch quantity the server last stated for this line (F34). */
  savedDispatchQuantity?: number;
  confirmedOrderItemIndex?: number | null;
  sourceType?: 'confirmed_order' | 'manual' | 'inventory' | 'catalogue' | 'supplier' | 'pantry';
  supplierName?: string;
  /** D86: the Vendor the supplier was picked as; null on an old line whose typed name matched none. */
  supplierVendorId?: string | null;
  itemName?: string;
  itemDescription?: string;
  inventoryItemId?: string;
  confirmedQuantity?: number;
  invoiceQuantity?: number;
  billQuantity?: number | null;
  billUnit?: string;
  unit?: string;
  quoteUnitPrice?: number | null;
  quoteLineTotal?: number | null;
  actualUnitCost?: number | null;
  /**
   * Which rung the cost stands on: 'po' (PO price), 'billed' (the supplier's bill), 'shipped', or a
   * person's: 'manual' (typed) / 'chosen' (picked on this line, D157.9), kept until sent back as null.
   * On an order line the server owns the cost unless it arrives a person's; a hand line never carries a system rung.
   */
  costBasis?: 'po' | 'billed' | 'shipped' | 'manual' | 'chosen' | null;
  actualLineCost?: number | null;
  combinedUnitCost?: number | null;
  /** The REALISED mark-up % (billed over cost; a line billed 0: unit price over unit cost, D119.19). Never the rate typed. */
  markupPercent?: number | null;
  /** D119.15: the mark-up % the line is meant to be priced at (typed, or the order's quote). null = none; never 0 for "none". */
  typedMarkupPercent?: number | null;
  markupAmount?: number | null;
  invoiceLineTotal?: number | null;
  varianceAmount?: number | null;
  variancePercent?: number | null;
  vatApplied?: boolean;
  vatPercent?: number | null;
  vatAmount?: number | null;
  discrepancyAlert?: string;
  /** D104.40: machine-readable kind of the quantity alert; read this, never the prose. */
  discrepancyKind?: '' | 'po_qty_differs' | 'po_rounded_to_case' | 'supplier_billed_short' | 'supplier_billed_over';
  /** D171.4: what the supplier billed for the line, in its billed unit, while less than it buys; D172.5: or more than it buys (`supplierBilledVs`); null otherwise. Server-owned. */
  supplierBilledQty?: number | null;
  /** D172.5: which side of what the line buys `supplierBilledQty` is on; null exactly when it is null (null beside a figure: a line stored before D172.5, 'short'). Server-owned. */
  supplierBilledVs?: 'short' | 'over' | null;
  poMatches?: Array<{
    poId?: string;
    poNumber?: string;
    /** The PO line's own number: with `poId`, which PO line this is (server `poLineOf`). */
    poLineNumber?: number | null;
    /** 'order_line' = the PO line was traced to this order line (the server owns the line's cost); 'name' = similarity only. */
    matchBasis?: 'order_line' | 'name' | null;
    /** The PO's vendor (VendorPurchaseOrder.vendorId); null on a PO with none. */
    vendorId?: string | null;
    vendorName?: string;
    quantity?: number;
    /** The unit `quantity` is in — the PO line's (may be its packs, e.g. CS). '' / absent on a match stored before 2026-09-28. */
    uom?: string;
    unitPrice?: number | null;
    totalPrice?: number | null;
    /** The PO line's currency (TT$ for a PO) — unitPrice/totalPrice are in it, not the invoice's. Null on a match stored before 2026-09-28. */
    currency?: string | null;
    /** The PO's price basis: 'before_vat' = unitPrice/totalPrice are before VAT (D82); null = VAT-inclusive (a pre-D82 PO, or a match stored before 2026-09-28). */
    priceBasis?: 'before_vat' | null;
  }>;
  packingQuantity?: number;
  packingUnit?: string;
  dispatchedQuantity?: number;
  dispatchQuantity?: number;
  /** Packing text typed for the next slip; null = not typed, the slip takes itemPackingDetails (D153.2). */
  dispatchPacking?: string | null;

  // Module 3 — worksheet columns
  descriptionAmended?: boolean;
  itemPackingDetails?: string;
  sourceSupplierNames?: string;
  clientRemarks?: string;
  internalRemarks?: string;
  coldStorageFlag?: boolean;
  completedTick?: boolean;

  // Module 4 — UOM Override Cascade
  uomOverridden?: boolean;
  originalSellingPrice?: number | null;
  originalBillUnit?: string;

  // Module 5 — Mark-up override
  markupOverridden?: boolean;

  // Module 6 — Item status (RED/PURPLE/PINK/ORANGE/BLUE)
  itemStatus?: '' | 'red' | 'purple' | 'pink' | 'orange' | 'blue';

  // Module 8 — Multi-source quantity entry
  qtyFromPantry?: number;
  qtyFromStock?: number;
  vendorQuantities?: Array<{ vendorName: string; vendorId?: string | null; quantity: number }>;
  totalQtyBilled?: number;

  // Section B — O/S/P source tagging
  sourceCodes?: Array<'O' | 'S' | 'P'>;

  // Vendor Source Traceability — structured per-line provenance (one entry per
  // place the line was filled from). Derived server-side; read-only here.
  //
  // derivedFrom tells you how much to trust an entry:
  //   'po_trail' — automatic, straight from the purchase orders for this line
  //   'po_match' — ops named a supplier and it matched a PO
  //   'manual'   — ops named a supplier, nothing backs it
  sourceProvenance?: Array<{
    kind: 'vendor' | 'stock' | 'pantry';
    vendorId?: string | null;
    vendorCode?: string;
    vendorName?: string;
    quantity?: number;
    vendorInvoices?: Array<{
      vendorInvoiceId?: string | null;
      invoiceNumber?: string;
      matchedBy?: 'item' | 'po_only';
    }>;
    vendorInvoiceId?: string | null;
    poId?: string | null;
    poNumber?: string;
    derivedFrom?: 'po_trail' | 'po_match' | 'manual' | 'stock' | 'pantry';
  }>;

  // Q2 — line added on invoice (not on original CO)
  isManualAddition?: boolean;

  // Phase 2 additions
  // H13/H14 — TT$/US$ cost per UOM
  ttCostPerUom?: number | null;
  usdCostPerUom?: number | null;
  fxRateAtCost?: number | null;
  // What the PO said beside what the bill says (server-written with the cost; only a billed cost has them): the
  // line panel strikes the old case conversion and TT$ cost through next to the new ones. Display only.
  poUnitCost?: number | null;
  poPackCount?: number | null;
  poPackText?: string | null;
  billedPackCount?: number | null;
  billedPackText?: string | null;
  billedFrom?: string | null;
  // H19 — USCL Remarks (internal, printed nowhere - D98.10; distinct from the client remark)
  usclRemarks?: string;
  // D119.12 — the pack the cost was quoted on ("SWISS SPOUCH KETCHUP · 750 ML"), stored at order build.
  // Internal: the drawer's Quote information shows it; never printed.
  quotedPack?: string | null;
  // O2 — delivery-order sort key
  deliveryOrder?: number | null;
  // P1/P2 — persisted reason for outstanding items
  notSuppliedReason?: string;
};

export type ClientInvoiceDto = {
  _id: string;
  invoiceNumber: string;
  source?: 'confirmed_order' | 'manual' | 'upload';
  confirmedOrderId: string;
  rfqId?: string | null;
  quotationId?: string | null;
  rfqNumber?: string;
  quotationNumber?: string;
  clientName?: string;
  clientEmail?: string;
  vesselName?: string;
  currency?: string;
  // The rates pinned on the invoice, TT$ per US$1 (invariant 5): the last currency conversion's, and the
  // Settings rate read when an invoice with no quotation was made. Read-only here (D119.9).
  currencyConversion?: { rate?: number | null } | null;
  fxRateAtCreation?: number | null;
  // Module 2 — header fields
  ownerName?: string;
  ownerAddress?: string;
  agentName?: string;
  // D153.6 — who pays, and the agent's address; read the payer through payerOf (none stated = the agent).
  payer?: InvoicePayer;
  agentAddress?: string;
  clientPoNumber?: string;
  // D183.5 order info, seeded once from the RFQ, never printed. estimatedDeliveryDate is yyyy-mm-dd or ''.
  port?: string;
  estimatedDeliveryDate?: string;
  category?: InvoiceCategory;
  status: ClientInvoiceStatus;
  accountingStatus?: 'not_sent' | 'accounting_team';
  invoiceDate?: string;
  dueDate?: string;
  // R1/R5/R6 register column (free text, e.g. "30 days net") — Item 5, 9 Oct QA retest.
  paymentTerms?: string;
  notes?: string;
  sentAt?: string | null;
  // D56 — a quotation's discount and charges, copied onto the order's MAIN
  // invoice ONLY (an `isAdditional` invoice keeps both at their defaults: 0,
  // []). `discountPercentage` is copied ONCE at generation and is never
  // patchable (patchClientInvoice's allow-list omits it on purpose — see
  // ClientInvoicePage.tsx's totals block). `charges` is the flattened,
  // editable {label, amount} row list, editable via PATCH only until the
  // invoice is sent (409 CLIENT_INVOICE_CHARGES_LOCKED afterwards).
  discountPercentage?: number;
  charges?: ClientInvoiceChargeDto[];
  isAdditional?: boolean;
  items: ClientInvoiceItemDto[];
  summary?: Record<string, any>;
  invoiceSection?: Record<string, any>;
  markupSection?: Record<string, any>;
  deliverySlipSection?: Record<string, any>;
  createdAt?: string;
  updatedAt?: string;
};

export type InvoicePayer = 'owner' | 'agent';

/** D153.6: who pays an invoice or credit note. A record that states none (older, or a lean read) is the agent. */
export const payerOf = (doc: { payer?: string | null } | null | undefined): InvoicePayer =>
  doc?.payer === 'owner' ? 'owner' : 'agent';

export type ClientInvoiceListResponse = {
  success: boolean;
  data: {
    clientInvoices: ClientInvoiceDto[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
};

export async function fetchClientInvoices(page = 1, limit = 25, search = '', status = 'all') {
  const q = new URLSearchParams({ page: String(page), limit: String(limit), status });
  if (search) q.set('search', search);
  const res = await get<ClientInvoiceListResponse>(`/client-invoices?${q.toString()}`);
  return res.data;
}

// One create-or-get POST in flight per order (B007). The invoice page's load effect runs it once per
// mount, and the dev build's StrictMode, a second mount or a double click run it twice at once: two
// requests 160 ms apart made two drafts when the server's unique index was not there to stop them.
// A second ask while one is in flight shares its answer; once it settles (either way) the entry goes.
const createOrGetInFlight = new Map<string, Promise<ClientInvoiceDto>>();

export function createOrGetClientInvoiceFromConfirmedOrder(confirmedOrderId: string): Promise<ClientInvoiceDto> {
  const running = createOrGetInFlight.get(confirmedOrderId);
  if (running) return running;
  const request = postJson<{ success: boolean; data: ClientInvoiceDto }>(
    `/client-invoices/from-confirmed/${confirmedOrderId}`,
    {}
  )
    .then((res) => res.data)
    .finally(() => createOrGetInFlight.delete(confirmedOrderId));
  createOrGetInFlight.set(confirmedOrderId, request);
  return request;
}

export async function fetchClientInvoiceById(id: string) {
  const res = await get<{ success: boolean; data: ClientInvoiceDto }>(`/client-invoices/${id}`);
  return res.data;
}

export async function fetchClientInvoiceByConfirmedOrderId(confirmedOrderId: string) {
  const res = await get<{ success: boolean; data: ClientInvoiceDto }>(
    `/client-invoices/confirmed-order/${confirmedOrderId}`
  );
  return res.data;
}

/**
 * A save of the invoice. `expectedUpdatedAt` is the invoice's `updatedAt` as the
 * screen loaded it: the server writes only if the invoice is still that version,
 * else refuses with `CLIENT_INVOICE_CHANGED` (D81 — an order quantity change the
 * invoice followed must not be undone by a screen opened before it).
 */
export type ClientInvoicePatch = Partial<ClientInvoiceDto> & {
  expectedUpdatedAt?: string;
  /** Marking sent with lines not dispatched: refused (409 CLIENT_INVOICE_UNDISPATCHED_LINES) unless true. */
  acknowledgeUndispatched?: boolean;
};

export async function patchClientInvoice(id: string, payload: ClientInvoicePatch) {
  const res = await patch<{ success: boolean; data: ClientInvoiceDto }>(`/client-invoices/${id}`, payload);
  return res.data;
}

// ─── Vendor Source Traceability — reverse trace ────────────────────────────

export type SourceTraceRow = {
  clientInvoiceId: string;
  invoiceNumber: string;
  invoiceDate: string | null;
  status: string;
  clientName: string;
  vesselName: string;
  lineNumber: number | null;
  itemName: string;
  itemDescription: string;
  unit: string;
  /** Quantity attributed to the traced source, not the whole line. */
  quantityFromSource: number;
  lineQuantity: number;
  vendorId: string | null;
  vendorCode: string;
  vendorName: string;
  poId: string | null;
  poNumber: string;
  derivedFrom: string;
  vendorInvoices: Array<{
    vendorInvoiceId: string | null;
    invoiceNumber: string;
    matchedBy: string;
  }>;
};

export type SourceTraceResult = {
  mode: 'vendor_invoice' | 'vendor' | 'purchase_order';
  subject: Record<string, string | null>;
  rows: SourceTraceRow[];
  totals: { lines: number; clientInvoices: number; vessels: number; quantity: number };
  vessels: string[];
};

export type SourceTraceQuery =
  | { vendorInvoiceId: string }
  | { vendorId: string }
  | { vendorName: string }
  | { poId: string };

/** "Which vessels received goods from this vendor / vendor invoice / PO?" */
export async function traceClientInvoiceSource(query: SourceTraceQuery) {
  const q = new URLSearchParams(query as Record<string, string>);
  const res = await get<{ success: boolean; data: SourceTraceResult }>(
    `/client-invoices/trace?${q.toString()}`
  );
  return res.data;
}

export async function createClientInvoiceDeliverySlip(id: string, payload: Record<string, any> = {}) {
  const res = await postJson<{ success: boolean; data: { invoice: ClientInvoiceDto; deliverySlip: any } }>(
    `/client-invoices/${id}/delivery-slips/create`,
    payload
  );
  return res.data;
}

/**
 * D96.4 - restate every price and cost on the invoice into `currency` once, at the invoice's
 * rate already fixed on the invoice (the server owns it, D119.16). Refusals are machine codes:
 * FX_RATE_NOT_SET, _UNCHANGED, CLIENT_INVOICE_LINES_LOCKED, CLIENT_INVOICE_CHANGED.
 */
export async function convertClientInvoiceCurrency(
  id: string,
  body: { currency: 'TTD' | 'USD'; expectedUpdatedAt?: string }
) {
  const res = await postJson<{ success: boolean; data: ClientInvoiceDto }>(
    `/client-invoices/${id}/convert-currency`,
    body
  );
  return res.data;
}

export async function createManualClientInvoice(payload: Record<string, any>) {
  const res = await postJson<{ success: boolean; data: ClientInvoiceDto }>('/client-invoices/manual', payload);
  return res.data;
}

export async function uploadManualClientInvoice(formData: FormData) {
  const res = await postFormData<{ success: boolean; data: ClientInvoiceDto }>('/client-invoices/manual', formData);
  return res.data;
}

// I1-I4 — UOM cascade helper: fetch the pack ratio + cost tiers for an item so
// the worksheet can recalc cost/selling/markup when the user changes billUnit.
// Returns null when we have no matching InventoryItem (the UI should just leave
// the values alone and rely on manual review).
export interface UomFactor {
  level2Name: string;
  level1Name: string;
  level1PerLevel2: number;
  weightUnit: 'kg' | 'lb' | null;
  weightPerLevel1: number | null;
  costLevels: {
    ttPerLevel2: number;
    ttPerLevel1: number;
    /**
     * The four WEIGHT tiers are `number | null`. Null is the server saying the
     * item states no weight, so it HAS no per-kg cost — never 0, which reads as
     * free (`costEngine.computeCostLevels`, invariant 8). The cascade below
     * already refuses to write a cost it cannot read, so a null lands on the
     * same branch a 0 did: leave the line's cost alone for a human to set.
     */
    ttPerKg: number | null;
    ttPerLb: number | null;
    usdPerLevel2: number;
    usdPerLevel1: number;
    usdPerKg: number | null;
    usdPerLb: number | null;
    vatInclusive: boolean;
  } | null;
  fxRateAtPurchase: number;
}

export async function resolveUomFactor(itemName: string): Promise<UomFactor | null> {
  if (!itemName || !itemName.trim()) return null;
  const res = await get<{ success: boolean; data: UomFactor | null }>(
    `/client-invoices/uom-factor?itemName=${encodeURIComponent(itemName)}`
  );
  return res.data;
}

/** D157.2b: Import Vendor Bill's list - the invoice's order's bills first (`matches`), then the search `q`. */
export async function listClientInvoiceVendorBills(id: string, q: string): Promise<CandidateInvoice[]> {
  const qs = q ? `?q=${encodeURIComponent(q)}` : '';
  const res = await get<{ success: boolean; data: CandidateInvoice[] }>(`/client-invoices/${encodeURIComponent(id)}/vendor-bills${qs}`);
  return res.data;
}

/** D171.1: a draft line as a bill is paired with it; `position` is its index in the draft. */
export interface DraftLineForPairing {
  position: number;
  itemName: string;
  itemDescription: string;
  unit: string;
  notSuppliedReason: string;
  poMatches: Array<{ poId: string; poLineNumber: number | null }>;
}

/** D171.1: bill line i's draft line (`target`, null: none), the quantity (null: none stated) and unit read off it, its
 * cost with its VAT, per that unit (the billed rung's figure, D172.8; null: no price stated) and the draft lines counted in another unit
 * (`unitsDiffer`), which nothing from it merges onto. */
export interface VendorBillLinePair {
  target: number | null;
  quantity: number | null;
  unit: string;
  unitPrice: number | null;
  unitsDiffer: number[];
}

/** A picked bill's lines for import (server `vendorBillLinesForImport`); `currency` null = it states none (D157.5). */
export interface VendorBillForImport {
  _id: string;
  currency: string | null;
  vendorId: string | null;
  vendorReconciliation: { matchedVendorId: string | null; matchedVendorName: string | null };
  extracted: { vendor: { name: string | null }; lineItems: any[] };
  /** One per line of `extracted.lineItems`, in order. */
  pairs: VendorBillLinePair[];
}

/** Paired with the draft's `lines` (a read). Refused with its code: an expense (`VENDOR_INVOICE_EXPENSE_NO_CLIENT_COST`)
 * or no lines (`VENDOR_INVOICE_NO_LINES`). */
export async function fetchVendorBillForImport(billId: string, lines: DraftLineForPairing[]): Promise<VendorBillForImport> {
  const res = await postJson<{ success: boolean; data: VendorBillForImport }>(`/client-invoices/vendor-bills/${encodeURIComponent(billId)}`, { lines });
  return res.data;
}

const fetchInvoicePrint = (
  id: string,
  print: 'pdf' | 'packing-list/pdf' | 'not-supplied/pdf' | 'customs-slip/pdf' | `delivery-slips/${string}/pdf` | `delivery-slips/${string}/packing-list/pdf`,
): Promise<Blob> => fetchServerPrint(`/client-invoices/${encodeURIComponent(id)}/${print}`);

/** The TAX INVOICE PDF (D153.4). */
export const fetchClientInvoicePdf = (id: string) => fetchInvoicePrint(id, 'pdf');

/** The PACKING LIST PDF: the invoice's lines on the invoice's pages, no prices (D153.5). */
export const fetchClientInvoicePackingListPdf = (id: string) => fetchInvoicePrint(id, 'packing-list/pdf');

/** The ITEMS NOT SUPPLIED PDF for the captain: the lines not supplied and why, never on the invoice (D170.7). */
export const fetchClientInvoiceNotSuppliedPdf = (id: string) => fetchInvoicePrint(id, 'not-supplied/pdf');

/** A customs invoice's slip PDF, built from the invoice (D157.17). */
export const fetchClientInvoiceCustomsSlipPdf = (id: string) => fetchInvoicePrint(id, 'customs-slip/pdf');

/** One DELIVERY (or EXCHANGE) SLIP PDF: the invoice's header and that slip's saved rows (D153.5). */
export const fetchClientInvoiceDeliverySlipPdf = (id: string, slipId: string) =>
  fetchInvoicePrint(id, `delivery-slips/${encodeURIComponent(slipId)}/pdf`);

/** One delivery slip's PACKING LIST PDF: what that delivery packed, from the slip's saved rows (D158.2). */
export const fetchClientInvoiceSlipPackingListPdf = (id: string, slipId: string) =>
  fetchInvoicePrint(id, `delivery-slips/${encodeURIComponent(slipId)}/packing-list/pdf`);

/** Accounts-screen cards: per-currency money owed (cancelled excluded) and a
 *  count per status, over the whole searched book rather than one list page. */
export type ClientInvoiceSummary = {
  totalsByCurrency: Record<string, number>;
  counts: Record<string, number> & { all: number };
};

export async function fetchClientInvoiceSummary(search = '') {
  const q = search ? `?${new URLSearchParams({ search }).toString()}` : '';
  const res = await get<{ success: boolean; data: ClientInvoiceSummary }>(`/client-invoices/summary${q}`);
  return res.data;
}

// ─── Worksheet actions (additional invoice, writeback, exchange slip, alerts) ───
// All on apiClient: its base URL, 401 handling and `{code, details}` error envelope.

/** Module 28 — a new additional invoice against the same client PO; `customsAmount` makes a customs invoice billing that fee (D157.17). */
export async function createAdditionalClientInvoice(id: string, customsAmount?: number) {
  const res = await postJson<{ success: boolean; data: ClientInvoiceDto }>(
    `/client-invoices/${encodeURIComponent(id)}/additional`,
    customsAmount === undefined ? {} : { kind: 'customs', amount: customsAmount }
  );
  return res.data;
}

/**
 * B6 — record the actual pick (+ TT$ cost) on a stock/pantry procurement-plan line.
 * `expectedUpdatedAt` is the invoice's updatedAt as the screen loaded it; a stale one is
 * refused 409 CLIENT_INVOICE_CHANGED.
 */
export async function writebackProcurementPlanLine(
  id: string,
  payload: {
    list: 'stock' | 'pantry';
    lineNumber: number;
    actualQuantity?: number;
    costPerUnitTt?: number;
    expectedUpdatedAt?: string;
  }
) {
  const res = await patch<{ success: boolean; data: ClientInvoiceDto }>(
    `/client-invoices/${encodeURIComponent(id)}/procurement-plan/writeback`,
    payload
  );
  return res.data;
}

export type ExchangeSwap = {
  returnedItemName: string;
  returnedQty: number;
  deliveredItemName: string;
  deliveredQty: number;
  reason: string;
};

/** Module 29 — net-zero exchange slip. Same `expectedUpdatedAt` contract as the writeback. */
export async function createClientInvoiceExchangeSlip(
  id: string,
  payload: { swaps: ExchangeSwap[]; expectedUpdatedAt?: string }
) {
  const res = await postJson<{ success: boolean; data: { slipNumber?: string } }>(
    `/client-invoices/${encodeURIComponent(id)}/exchange-slip`,
    payload
  );
  return res.data;
}

/** J8 — upsert the raised alerts into the invoice's alertHistory (idempotent by alertKey). */
export async function syncClientInvoiceAlerts(id: string, alerts: unknown[]) {
  return postJson<{ success: boolean }>(`/client-invoices/${encodeURIComponent(id)}/alerts/sync`, { alerts });
}

/** J9 — record a dismissal (with the reason) in alertHistory. */
export async function dismissClientInvoiceAlert(id: string, alertKey: string, reason: string) {
  return postJson<{ success: boolean }>(
    `/client-invoices/${encodeURIComponent(id)}/alerts/${encodeURIComponent(alertKey)}/dismiss`,
    { reason }
  );
}
